import { AbstractControl, FormArray, FormGroup } from '@angular/forms';

/**
 * Force every control in a form tree to re-run its own validators against its
 * CURRENT value, bottom-up.
 *
 * WHY THIS EXISTS
 * ---------------
 * A very common bug across our forms: a Save/Submit handler gates on
 * `form.invalid` and silently `return`s, and the button appears to "do nothing"
 * — yet a full page refresh makes the exact same data submit fine.
 *
 * The cause is a STALE validity status. When a form is populated
 * programmatically (`patchValue`/`reset`) and its fields are rendered through
 * custom ControlValueAccessors (date picker, phone, postcode, select, etc.),
 * a child accessor can write its value and leave the control's status as
 * INVALID even though the value is now valid. Crucially, calling
 * `updateValueAndValidity()` on the parent FormGroup does NOT fix this: on a
 * group it only re-aggregates the children's EXISTING statuses — it never asks
 * each child to re-validate. So the stale INVALID survives and blocks submit.
 * A full reload rebuilds the form from scratch, which is why "refresh fixes it".
 *
 * Calling this before the validity gate re-validates each leaf control against
 * its real value, so the gate reflects the true state.
 *
 * USAGE (drop into any submit handler before the invalid check):
 *   onSubmit(): void {
 *     this.form.markAllAsTouched();
 *     revalidateFormTree(this.form);
 *     if (this.form.invalid) { scrollToFirstInvalidControl(host); return; }
 *     ...emit / call API...
 *   }
 */
export function revalidateFormTree(control: AbstractControl | null | undefined): void {
  if (!control) {
    return;
  }

  if (control instanceof FormGroup) {
    for (const child of Object.values(control.controls)) {
      revalidateFormTree(child);
    }
    control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    return;
  }

  if (control instanceof FormArray) {
    for (const child of control.controls) {
      revalidateFormTree(child);
    }
    control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    return;
  }

  // Leaf FormControl: re-run its own validators against its current value.
  control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
}

/**
 * Flush browser-autofilled values from the DOM into the reactive form.
 *
 * Chrome fires an `input` event when it autofills, so Angular's value accessor
 * updates the control. Firefox and Safari frequently DON'T: they set the input's
 * DOM value with no `input`/`change` event and no `:-webkit-autofill` animation.
 * If the user never focuses those fields (e.g. a browser fills email/name on
 * load), their controls stay empty even though the field visibly has text — so
 * the form is invalid and Save silently does nothing. A full reload can re-fill
 * differently, which is why it looks browser- and machine-specific.
 *
 * Call this at the very start of a submit handler (before validating). It finds
 * every input/textarea managed by AutofillSyncDirective (marked with the
 * `data-autofill-sync` attribute, so custom search/select inputs are left alone)
 * and re-dispatches the real `input` event the value accessor listens to —
 * synchronously, cross-browser.
 */
export function commitAutofilledValues(host: HTMLElement | null | undefined): void {
  if (!host || typeof host.querySelectorAll !== 'function') {
    return;
  }

  const fields = host.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    '[data-autofill-sync]',
  );

  fields.forEach((field) => {
    if (field.readOnly || field.disabled) {
      return;
    }

    if (field instanceof HTMLInputElement && field.type === 'number') {
      return;
    }

    field.dispatchEvent(new Event('input', { bubbles: true }));
  });
}

/**
 * Log every control blocking a submit, as `path -> errors`.
 *
 * Pair this with the `form.invalid` gate: when a Save "does nothing", the console
 * says exactly which field is at fault — including ones scrolled out of view, in
 * a collapsed section, or with no visible error text of their own.
 */
export function logInvalidControls(
  control: AbstractControl | null | undefined,
  label = 'Form',
): void {
  const invalid = collectInvalidControls(control, '');

  if (invalid.length === 0) {
    return;
  }

  console.warn(
    `[${label}] blocked by ${invalid.length} invalid field(s):`,
    invalid.map(({ path, errors }) => ({ field: path, errors })),
  );
}

function collectInvalidControls(
  control: AbstractControl | null | undefined,
  path: string,
): { path: string; errors: Record<string, unknown> }[] {
  if (!control || control.valid) {
    return [];
  }

  if (control instanceof FormGroup) {
    return Object.entries(control.controls).flatMap(([name, child]) =>
      collectInvalidControls(child, path ? `${path}.${name}` : name),
    );
  }

  if (control instanceof FormArray) {
    return control.controls.flatMap((child, index) =>
      collectInvalidControls(child, `${path}[${index}]`),
    );
  }

  // Leaf control: this is the field the user actually has to fix.
  return [{ path: path || '(root)', errors: control.errors ?? {} }];
}

/**
 * Every way a field in this codebase advertises "I'm invalid": Angular's own
 * class, our reusable inputs' aria/data flags, and the red-border utilities used
 * by the hand-rolled tile/radio groups (which have no aria-invalid of their own).
 */
const INVALID_FIELD_SELECTOR =
  '.ng-invalid, [aria-invalid="true"], [data-invalid="true"], ' +
  '.border-red500, .border-red600, .border-red500\\/60, .border-red400';

/**
 * Rendered validation text — the scroller's fallback. Some forms (popups with their
 * own `errors` map, the hand-rolled yes/no + tile groups) never produce an
 * `ng-invalid` element at all; the red message they print is the only trace.
 *
 * Deliberately limited to block-level message elements: the required-field asterisks
 * are `<span class="text-red600">*</span>`, and matching those would point the
 * scroller at every labelled field on the page.
 */
const ERROR_MESSAGE_SELECTOR =
  'p.text-red600, p.text-red500, p.text-red400, small.text-red600, small.text-red500, .field-error';

/**
 * Scroll the first invalid field inside `host` into view (and focus it) so an
 * invalid submit is never a silent dead-end.
 *
 * Deferred past a tick and two paint frames: the tick lets Angular's zone
 * process the `markAllAsTouched()` that normally precedes this call, the first
 * frame gets the `ng-invalid`/error classes stamped into the DOM, and the second
 * leaves the layout stable enough to measure.
 *
 * Only LEAF invalid elements count — a form or form-group also carries
 * `ng-invalid`, and scrolling to it would just land at the top of the form
 * instead of on the field that's actually wrong.
 */
export function scrollToFirstInvalidControl(host: HTMLElement | null | undefined): void {
  if (!host || typeof host.querySelectorAll !== 'function') {
    return;
  }

  setTimeout(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const target = findFirstInvalidField(host);
        if (!target) {
          return;
        }

        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Focus after the smooth scroll has settled, otherwise focus() fights it
        // with a second jump.
        setTimeout(() => resolveFocusable(target)?.focus({ preventScroll: true }), 350);
      });
    });
  });
}

/** First invalid leaf field inside `host`, in document order; null if none is on screen. */
function findFirstInvalidField(host: HTMLElement): HTMLElement | null {
  const all = Array.from(host.querySelectorAll<HTMLElement>(INVALID_FIELD_SELECTOR));
  // Elements hidden behind a collapsed @if have no client rects — never scroll to them.
  const visible = all.filter((el) => el.getClientRects().length > 0);
  const leaves = visible.filter((el) => !el.querySelector(INVALID_FIELD_SELECTOR));
  // A nested <form> can be a "leaf" without being a field — e.g. postcode-reusable's
  // internal form stays ng-invalid from its hidden address/city controls, which used
  // to drag every blocked save up to the address block. Prefer real fields over it.
  const fieldLeaves = leaves.filter((el) => el.tagName.toUpperCase() !== 'FORM');
  if (fieldLeaves.length === 0) {
    // Only form wrappers are flagged — a hand-rolled field's red message is the better target.
    const messageField = findFirstErrorMessageField(host);
    if (messageField) {
      return messageField;
    }
  }
  const pool = fieldLeaves.length > 0 ? fieldLeaves : leaves.length > 0 ? leaves : visible;

  if (pool.length === 0) {
    // No control advertises itself as invalid — fall back to the first red message
    // actually on screen, which is all a hand-rolled error map leaves behind.
    return findFirstErrorMessageField(host);
  }

  return sortDocumentOrder(pool)[0];
}

/**
 * The field that owns the first rendered error message inside `host`: its
 * `[data-field]` wrapper when it has one, otherwise the message's own parent, so
 * the label and the input scroll into view together rather than just the text.
 */
function findFirstErrorMessageField(host: HTMLElement): HTMLElement | null {
  const messages = Array.from(host.querySelectorAll<HTMLElement>(ERROR_MESSAGE_SELECTOR)).filter(
    (el) => (el.textContent ?? '').trim().length > 0 && el.getClientRects().length > 0,
  );

  const first = sortDocumentOrder(messages)[0];
  if (!first) {
    return null;
  }

  return (first.closest('[data-field]') as HTMLElement | null) ?? first.parentElement ?? first;
}

/**
 * Document order. `querySelectorAll` already returns it, but sort explicitly so
 * CSS reordering (flex `order`, grid placement) can't put a later field first.
 */
function sortDocumentOrder(elements: HTMLElement[]): HTMLElement[] {
  return [...elements].sort((a, b) => {
    const relation = a.compareDocumentPosition(b);
    if (relation & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (relation & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  });
}

/** The native control to focus — the element itself, or the input our reusable wrappers own. */
function resolveFocusable(el: HTMLElement): HTMLElement | null {
  const tag = el.tagName.toUpperCase();
  if (
    (tag === 'INPUT' && (el as HTMLInputElement).type !== 'hidden') ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT'
  ) {
    return el;
  }

  return el.querySelector<HTMLElement>(
    'input:not([type="hidden"]):not([disabled]),' +
      'textarea:not([disabled]),' +
      'select:not([disabled]),' +
      'button[type="button"]',
  );
}

/**
 * The value a Yes/No (single-choice) click leaves behind: the picked answer, or `null`
 * when it was already the selected one — a second click on the same answer deselects it.
 */
export function toggledChoice<T>(current: unknown, picked: T): T | null {
  return current === picked ? null : picked;
}
